import { Component, ElementRef, ChangeDetectorRef, OnInit, OnDestroy, Input, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ReviewStoreV2, ReviewModeV2, ReviewDraftV2 } from '../review-store-v2.service';
import { ToastBarService } from '../../../../../services/toast-bar.service';
import { INotification } from '../../../../../models/template/notifications-model.interface';
import { clampPinPercent, commentNumbers } from '../review-utils';

interface PinInfo {
  comment: INotification;
  number: number;
  x: number;
  y: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

@Component({
  selector: 'app-annotation-layer-v2',
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './annotation-layer-v2.component.html',
  styleUrl: './annotation-layer-v2.component.scss',
})
export class AnnotationLayerV2Component implements OnInit, OnDestroy {
  @Input({ required: true }) blockId!: string;

  mode: ReviewModeV2 = 'off';
  movingCommentId: string | null = null;
  draft: ReviewDraftV2 | null = null;
  viewing: number | null = null;
  latest: number | null = null;
  pins: PinInfo[] = [];
  activeCommentId: string | null = null;

  draftText = '';

  /** Lifts this block's layer above the following blocks' layers while its popup is open. */
  @HostBinding('class.has-popup')
  get hasPopup(): boolean {
    return !!this.draft;
  }

  composerOpen = false;
  composerPin: ReviewDraftV2 | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private store: ReviewStoreV2,
    private toast: ToastBarService,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.store.state$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.mode = state.mode;
      this.movingCommentId = state.movingCommentId;
      this.viewing = state.viewing;
      this.latest = state.latest;
      this.activeCommentId = state.activeCommentId;
      this.composerOpen = state.composerOpen;
      this.composerPin = state.composerPin?.blockId === this.blockId ? state.composerPin : null;

      const draft = state.draft?.blockId === this.blockId ? state.draft : null;
      if (draft !== this.draft) {
        this.draftText = '';
      }
      this.draft = draft;

      const commentNums = commentNumbers(state.comments);
      this.pins = state.comments
        .filter(
          (c) =>
            c.blockId === this.blockId &&
            c.version != null &&
            c.version === state.viewing &&
            c.xPercent != null &&
            c.yPercent != null
        )
        .map((c) => ({
          comment: c,
          number: commentNums.get(c.notificationId!) ?? 0,
          x: clampPinPercent(c.xPercent!),
          y: clampPinPercent(c.yPercent!),
        }));

      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get createActive(): boolean {
    return this.mode === 'annotate' && !this.movingCommentId && !this.composerOpen;
  }

  get pickActive(): boolean {
    return !!this.movingCommentId;
  }

  get composerActive(): boolean {
    return this.composerOpen && this.mode !== 'off' && !this.movingCommentId;
  }

  get popupLeft(): number {
    if (!this.draft) return 0;
    return clamp(this.draft.x, 8, 74);
  }

  get popupTop(): number {
    if (!this.draft) return 0;
    return clamp(this.draft.y, 8, 72);
  }

  onOverlayClick(event: MouseEvent): void {
    const rect = this.host.nativeElement.getBoundingClientRect();
    const x = clampPinPercent(((event.clientX - rect.left) / rect.width) * 100);
    const y = clampPinPercent(((event.clientY - rect.top) / rect.height) * 100);
    event.stopPropagation();

    if (this.pickActive) {
      const movingComment = this.store.snapshot.comments.find(
        (c) => c.notificationId === this.movingCommentId
      );
      if (movingComment) {
        this.store.movePin(movingComment, this.blockId, x, y).subscribe({
          error: () => this.toast.showToastError('Could not move the annotation.'),
        });
      }
      return;
    }

    if (this.composerActive) {
      this.store.setComposerPin(this.blockId, x, y);
      return;
    }

    if (this.createActive) {
      if (this.store.snapshot.latest === null) {
        this.toast.showToast({ severity: 'info', summary: 'Info', detail: 'Pull a version into review first (New version).' });
        return;
      }
      if (this.store.snapshot.viewing !== this.store.snapshot.latest) {
        this.toast.showToast({ severity: 'info', summary: 'Info', detail: `Comments can only be added on Version ${this.latest}.` });
        return;
      }
      this.store.openDraft(this.blockId, x, y);
    }
  }

  onPinClick(event: MouseEvent, pin: PinInfo): void {
    event.stopPropagation();
    if (pin.comment.notificationId) {
      this.store.focusComment(pin.comment.notificationId);
    }
  }

  submitDraft(): void {
    const text = this.draftText.trim();
    if (!text) return;
    if (!this.draft) return;

    this.store.addComment(text, this.blockId, { x: this.draft.x, y: this.draft.y }).subscribe({
      next: () => {
        this.draftText = '';
      },
      error: () => this.toast.showToastError('Could not save the comment.'),
    });
  }

  cancelDraft(): void {
    this.store.cancelDraft();
    this.draftText = '';
  }

  onDraftKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      this.submitDraft();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelDraft();
    }
  }

  trackPin(_: number, pin: PinInfo): string {
    return pin.comment.notificationId ?? '';
  }
}
